/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { SpeechToTextMessage } from '../service/websocket/useSpeechToTextSocket';

// audioStream constraints
const constraints = {
  audio: true,
  video: false,
};

const useAudioStreamer = (socket: Socket) => {
  const [input, setInput] = useState<MediaStreamAudioSourceNode | null>();
  const [globalStream, setGlobalStream] = useState<MediaStream | undefined>();
  const [processor, setProcessor] = useState<AudioWorkletNode | null>();
  const [audioContext, setAudioContext] = useState<AudioContext | undefined>();

  let AudioContext: {
    new (_contextOptions?: AudioContextOptions | undefined): AudioContext;
    prototype: AudioContext;
  } | null = null;

  /**
   * Initializes the audio streamer by setting up the AudioContext.
   */
  const initAudioStreamer = () => {
    if (AudioContext) {
      return;
    }

    if (window.AudioContext) {
      AudioContext = window.AudioContext;
    } else if ((window as any).webkitAudioContext) {
      AudioContext = (window as any).webkitAudioContext;
      // AudioContext is not defined in the global window object in Safari or iOS Safari. We need to use the
      // webkitAudioContext. Hence, need to use any to avoid typescript errors
    } else {
      throw new Error('AudioContext is not defined in the browser');
    }

    socket.connect();
  };

  /**
   * Stops recording and closes everything down. Runs on error or on stop.
   */
  const closeAll = () => {
    // Clear the listeners (prevents issue if opening and closing repeatedly)
    socket.off(SpeechToTextMessage.SENTENCE_TRANSCRIPTION);
    socket.off(SpeechToTextMessage.GOOGLE_CLOUD_STREAM_ERROR);
    socket.disconnect();
    socket.close();

    const tracks = globalStream ? globalStream.getTracks() : null;
    const track = tracks ? tracks[0] : null;
    if (track) {
      track.stop();
    }

    if (processor) {
      if (input) {
        try {
          input.disconnect(processor);
        } catch (error) {
          toast.error('Attempt to disconnect input failed.');
        }
      }

      if (audioContext?.destination) {
        processor.disconnect(audioContext.destination);
      } else {
        processor.disconnect();
      }
    }

    if (audioContext) {
      audioContext.close().then(() => {
        setInput(null);
        setAudioContext(undefined);
        AudioContext = null;
      });
    }
  };

  /**
   * Initializes the recording by setting up the audio context and the media stream.
   * @param onSentenceData
   * @param onLiveTranscription
   * @param onError
   */
  const initRecording = (
    onSentenceData: (_arg0: any) => void,
    onLiveTranscription: (_arg0: any) => void,
    onError: (_arg0: string) => void
  ) => {
    if (!AudioContext) {
      throw new Error('AudioContext is not defined');
    }

    const resolveLanguageBundle = (): string => {
      const lang = window.localStorage.getItem('i18nextLng');

      switch (lang) {
        case 'en':
          return 'en-US';
        case 'pt':
          return 'pt-BR';
        case 'ar':
          return 'ar-DZ';
        default:
          return 'en-US';
      }
    };

    socket.emit(SpeechToTextMessage.START_GOOGLE_CLOUD_STREAM, {
      config: {
        encoding: 'LINEAR16',
        sampleRateHertz: 16000,
        languageCode: resolveLanguageBundle(),
        profanityFilter: false,
        enableWordTimeOffsets: true,
      },
      interimResults: true, // If you want interim results, set this to true
    }); // init socket Google Speech Connection

    const sendAudio = (buffer: ArrayBufferLike) => {
      socket.emit(SpeechToTextMessage.BINARY_AUDIO_DATA, buffer);
    };

    const handleSuccess = async (stream: MediaStream) => {
      if (!AudioContext) {
        throw new Error('AudioContext is not defined');
      }
      const mediaAudioContext = new AudioContext({
        latencyHint: 'interactive',
      });
      setAudioContext(mediaAudioContext);
      if (!mediaAudioContext) {
        throw new Error('hook local AudioContext is not defined');
      }

      setGlobalStream(stream);
      const mediaStreamInput = mediaAudioContext.createMediaStreamSource(stream);
      setInput(mediaStreamInput);

      if (!mediaStreamInput) {
        throw new Error('hook local mediaStreamInput is not defined');
      }

      await mediaAudioContext.audioWorklet.addModule('/assets/webWorkers/audio-processor.js');
      mediaAudioContext.resume();

      const audioWorkletProcessor = new AudioWorkletNode(mediaAudioContext, 'recorder.worklet');
      setProcessor(audioWorkletProcessor);
      if (!audioWorkletProcessor) {
        throw new Error('hook local audioWorkletProcessor is not defined');
      }

      audioWorkletProcessor.connect(mediaAudioContext.destination);
      mediaAudioContext.resume();
      mediaStreamInput.connect(audioWorkletProcessor);

      audioWorkletProcessor.port.onmessage = (event: MessageEvent<ArrayBufferLike>) => {
        const audioData = event.data;
        sendAudio(audioData);
      };
    };

    navigator.mediaDevices.getUserMedia(constraints).then(handleSuccess);

    // Bind the data handler callback
    if (onSentenceData) {
      socket.on(SpeechToTextMessage.SENTENCE_TRANSCRIPTION, (data: string) => {
        onSentenceData(data);
      });
    }

    if (onLiveTranscription) {
      socket.on(SpeechToTextMessage.LIVE_SENTENCE, (data: string) => {
        onLiveTranscription(data);
      });
    }

    socket.on(SpeechToTextMessage.GOOGLE_CLOUD_STREAM_ERROR, (error: string) => {
      if (onError) {
        onError(error);
      }
      // We don't want to emit another end stream event
      closeAll();
    });
  };

  const stopRecording = () => {
    socket.emit(SpeechToTextMessage.END_GOOGLE_CLOUD_STREAM, '');
    closeAll();
  };

  return {
    initAudioStreamer,
    initRecording,
    stopRecording,
    closeAll,
  };
};

export default useAudioStreamer;
